/**
 * Shop-level settings, stored in `ShopTable.systemSettings`.
 *
 * That attribute is a LIST of `{key, value}` pairs with dotted keys —
 * `tax.percentage`, `serviceCharges.isEnabled`, `orderOptions.isRooms` — and
 * every value is a string. The POS apps have written it for years; this
 * module is the dashboard's first use of it.
 *
 * The merge is the important part. Writing `systemSettings` through
 * `updateShop` REPLACES the whole list, so a screen that sends only its own
 * keys would silently wipe a shop's tax and service-charge settings. Nothing
 * here ever builds a list from scratch: `mergeSettings` starts from what the
 * shop already has.
 */

export interface KeyValue {
  key: string;
  value: string;
}

/**
 * When the shop's trading day rolls over, as `HH:MM` (H3).
 *
 * A bar closing at 02:00 is still serving "yesterday", so a calendar midnight
 * would reset its prep counts mid-service. The POS, the cloud and this
 * dashboard all decide "is this prep count today's?" and used to answer
 * differently; this is the one value they read.
 */
export const STOCK_DAY_START_KEY = 'stock.dayStartTime';
export const DEFAULT_DAY_START = '04:00';

export const SHIPPING_ENABLED_KEY = 'productShipping.isEnabled';
export const SHIPPING_BASE_FEE_KEY = 'productShipping.baseFee';

const asList = (settings: unknown): KeyValue[] =>
  Array.isArray(settings)
    ? (settings as any[]).filter((s) => s && typeof s.key === 'string')
    : [];

/** The raw string stored under a key, or undefined. */
export function readSetting(settings: unknown, key: string): string | undefined {
  return asList(settings).find((s) => s.key === key)?.value;
}

/**
 * A stored boolean.
 *
 * Values arrive as the strings "true"/"false", which is how the POS writes
 * them. Anything else — absent, empty, junk — is false: a setting nobody has
 * deliberately turned on is off.
 */
export function readBool(settings: unknown, key: string): boolean {
  return String(readSetting(settings, key) ?? '').trim().toLowerCase() === 'true';
}

/** A stored number, or null when absent or unparseable. */
export function readNumber(settings: unknown, key: string): number | null {
  const raw = readSetting(settings, key);
  if (raw == null || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Apply a patch to a shop's settings, preserving every key it does not name.
 *
 * Returns a NEW list. A patch value of `null` removes the key, which is how a
 * setting is cleared rather than written as the string "null".
 */
export function mergeSettings(
  existing: unknown,
  patch: Record<string, string | number | boolean | null>,
): KeyValue[] {
  const out = asList(existing).map((s) => ({key: s.key, value: s.value}));

  for (const [key, value] of Object.entries(patch)) {
    const idx = out.findIndex((s) => s.key === key);
    if (value === null) {
      if (idx >= 0) out.splice(idx, 1);
      continue;
    }
    const str = String(value);
    if (idx >= 0) out[idx] = {key, value: str};
    else out.push({key, value: str});
  }

  return out;
}

export interface ShippingSettings {
  enabled: boolean;
  /** The shop-wide default, in the shop's currency. */
  baseFee: number;
}

/** A shop's product-shipping configuration. */
export function shippingSettings(shop: any): ShippingSettings {
  const settings = shop?.systemSettings;
  return {
    enabled: readBool(settings, SHIPPING_ENABLED_KEY),
    baseFee: readNumber(settings, SHIPPING_BASE_FEE_KEY) ?? 0,
  };
}

/** The patch that turns the shipping settings into stored pairs. */
export function shippingPatch(
  enabled: boolean,
  baseFee: number | string | null,
): Record<string, string | null> {
  const fee = Number(baseFee);
  return {
    [SHIPPING_ENABLED_KEY]: enabled ? 'true' : 'false',
    // Cleared rather than stored as "0" when blank, so "never set" and
    // "deliberately free" stay distinguishable in the raw data.
    [SHIPPING_BASE_FEE_KEY]:
      baseFee === '' || baseFee == null || !Number.isFinite(fee)
        ? null
        : String(fee),
  };
}

/**
 * What a product costs to ship.
 *
 * The rule: a product's own fee OVERRIDES the shop's base fee, and a product
 * with no fee of its own falls back to the base. An explicit 0 is a real
 * answer — "this one ships free" — so it is not treated as missing, which is
 * why the check is against null/'' rather than falsiness.
 *
 * Returns null when shipping is off for the shop: there is no fee at all,
 * which is different from a fee of zero.
 */
export function shippingFeeFor(product: any, shop: any): number | null {
  const {enabled, baseFee} = shippingSettings(shop);
  if (!enabled) return null;

  const own = product?.shippingFee;
  if (own === null || own === undefined || String(own).trim() === '') {
    return baseFee;
  }
  const n = Number(own);
  return Number.isFinite(n) ? n : baseFee;
}

/** The shop's day-start time, `HH:MM`, defaulted and validated. */
export function dayStartOf(shop: any): string {
  const raw = readSetting(shop?.systemSettings, STOCK_DAY_START_KEY);
  return isDayStart(raw) ? (raw as string) : DEFAULT_DAY_START;
}

/** Is this a usable `HH:MM`? Anything else falls back to the default. */
export function isDayStart(value: unknown): boolean {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(value ?? '').trim());
  return !!m;
}

/** The patch that stores the day start. */
export function dayStartPatch(value: string | null): Record<string, string | null> {
  return {[STOCK_DAY_START_KEY]: isDayStart(value) ? String(value).trim() : null};
}

/**
 * The trading day an instant belongs to, as `YYYY-MM-DD`.
 *
 * Mirror of electron-react-boilerplate/src/shared/stockLedger.ts `tradingDay`.
 */
export function tradingDay(iso: string | Date, dayStart: string = DEFAULT_DAY_START): string {
  const at = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(at.getTime())) return '';

  const [h, m] = String(isDayStart(dayStart) ? dayStart : DEFAULT_DAY_START)
    .split(':')
    .map((x) => Number(x));
  const startMinutes = h * 60 + m;

  const local = new Date(at.getTime());
  if (local.getHours() * 60 + local.getMinutes() < startMinutes) {
    local.setDate(local.getDate() - 1);
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`;
}

/** Is this count from the trading day in progress? */
export function isSameTradingDay(
  iso: string | null | undefined,
  now: string | Date = new Date(),
  dayStart: string = DEFAULT_DAY_START,
): boolean {
  if (!iso) return false;
  const day = tradingDay(iso, dayStart);
  return !!day && day === tradingDay(now, dayStart);
}
